import { useState, useEffect, useCallback } from 'react'
import { useDrawer, DrawerFooter } from '@/components/ui/drawer'
import { DrawerSkeleton } from '@/components/ui/drawer/DrawerSkeleton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useDrawerForm } from '@/hooks/useDrawerForm'
import { useDrawerKeyboard } from '@/hooks/useDrawerKeyboard'
import { useDrawerMessaging } from '@/hooks/useDrawerMessaging'
import { tenantsService, type Tenant, type CreateTenantDto, type UpdateTenantDto } from '@/services/tenants'
import { lendersService, type Lender, type TenantLender, type CreateLenderDto } from '@/services/lenders'
import { toast } from 'sonner'
import { Building2, Mail, Phone, User, Calendar, Package, Landmark, Plus, X, ExternalLink, Search } from 'lucide-react'

interface TenantFormDrawerProps {
  tenantId?: string
  onSuccess?: (tenant: Tenant) => void
}

// Store form state globally for access by footer
const formStates = new Map<string, {
  handleSave: () => Promise<void>
  isDirty: boolean
  saving: boolean
}>()

export function TenantFormDrawer({ tenantId, onSuccess }: TenantFormDrawerProps) {
  const { closeDrawer, updateDrawer } = useDrawer()
  const { broadcast } = useDrawerMessaging(`tenant-form-${tenantId || 'new'}`)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [dataLoaded, setDataLoaded] = useState(false)
  
  const isEditMode = !!tenantId
  const drawerId = `tenant-form-${tenantId || 'new'}`
  
  const [formData, setFormData] = useState<CreateTenantDto>({
    company_name: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
  })

  const [status, setStatus] = useState<'active' | 'suspended' | 'trial'>('active')
  const [plan, setPlan] = useState<string>('Basic')
  const [paymentProvider, setPaymentProvider] = useState<'stripe' | 'nmi'>('stripe')
  const [hasBlockingSubscription, setHasBlockingSubscription] = useState(false)

  // Lenders state
  const [allLenders, setAllLenders] = useState<Lender[]>([])
  const [tenantLenders, setTenantLenders] = useState<TenantLender[]>([])
  const [showLenderSearch, setShowLenderSearch] = useState(false)
  const [lenderSearchQuery, setLenderSearchQuery] = useState('')
  const [showCreateLenderDialog, setShowCreateLenderDialog] = useState(false)
  const [newLenderData, setNewLenderData] = useState<CreateLenderDto>({
    name: '',
    description: '',
    url: '',
    project_type: 'commercial'
  })
  const [savingLender, setSavingLender] = useState(false)

  // Load all lenders and tenant-specific lenders
  useEffect(() => {
    const loadLenders = async () => {
      try {
        const lenders = await lendersService.listActive()
        setAllLenders(lenders)

        if (isEditMode && tenantId) {
          const assigned = await lendersService.getTenantLenders(tenantId)
          setTenantLenders(assigned)
        }
      } catch (error) {
        console.error('Failed to load lenders:', error)
      }
    }

    loadLenders()
  }, [isEditMode, tenantId])

  // Use drawer form hook for auto-save and dirty tracking
  const {
    isDirty,
    markAsSaved,
    setInitialData,
  } = useDrawerForm(formData, setFormData, {
    drawerId,
    autoSave: true,
    autoSaveInterval: 30000,
    confirmClose: true,
    onAutoSave: async (data) => {
      if (isEditMode && tenantId) {
        try {
          await tenantsService.update(tenantId, { ...data, status })
          toast.success('Auto-saved')
        } catch (error) {
          console.error('Auto-save failed:', error)
        }
      }
    },
  })

  // Load tenant data if editing - only once. Lives below useDrawerForm so the
  // loaded record can be set as the pristine snapshot for dirty tracking.
  useEffect(() => {
    if (isEditMode && tenantId && !dataLoaded) {
      const loadTenant = async () => {
        setLoading(true)

        try {
          const data = await tenantsService.get(tenantId)
          setTenant(data)
          const loadedForm = {
            company_name: data.company_name,
            contact_name: data.contact_name,
            contact_phone: data.contact_phone,
            contact_email: data.contact_email,
          }
          setFormData(loadedForm)
          setInitialData(loadedForm)
          setStatus(data.status || 'active')
          setPlan(data.subscription_plan || 'Basic')
          setPaymentProvider(data.payment_provider || 'stripe')
          setDataLoaded(true)

          // The processor is pinned while a subscription depends on it
          import('@/services/billing').then(({ billingService }) =>
            billingService
              .tenantSummary(tenantId)
              .then((summary) =>
                setHasBlockingSubscription(!!summary.active_subscription)
              )
              .catch(() => setHasBlockingSubscription(false))
          )
        } catch (error) {
          console.error('Failed to load tenant:', error)
          toast.error('Failed to load tenant details')
        } finally {
          setLoading(false)
        }
      }

      loadTenant()
    }
  }, [isEditMode, tenantId, dataLoaded, setInitialData])

  const isValidEmail = useCallback((email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }, [])

  const validateForm = useCallback((): boolean => {
    const errors: string[] = []

    if (!formData.company_name.trim()) {
      errors.push('Company name is required')
    }
    if (!formData.contact_name.trim()) {
      errors.push('Contact name is required')
    }
    if (!formData.contact_email.trim()) {
      errors.push('Contact email is required')
    }
    if (formData.contact_email && !isValidEmail(formData.contact_email)) {
      errors.push('Invalid email format')
    }

    if (errors.length > 0) {
      toast.error(errors[0])
      return false
    }

    return true
  }, [formData, isValidEmail])

  const handleSave = useCallback(async () => {
    if (!validateForm()) return
    
    setSaving(true)
    
    try {
      let savedTenant: Tenant
      
      if (isEditMode && tenantId) {
        const updateData: UpdateTenantDto = {
          ...formData,
          status,
          payment_provider: paymentProvider,
        }
        savedTenant = await tenantsService.update(tenantId, updateData)
        toast.success('Tenant updated successfully')
      } else {
        savedTenant = await tenantsService.create(formData)
        toast.success('Tenant created successfully')
      }
      
      markAsSaved()
      
      // Send refresh message to other drawers
      broadcast('refresh', { tenantId: savedTenant.tenant_id })
      
      // Call success callback
      onSuccess?.(savedTenant)
      
      // Show success animation and close
      updateDrawer(drawerId, { loadingState: 'success' })
      setTimeout(() => {
        closeDrawer(drawerId)
      }, 1000)
    } catch (error) {
      console.error('Failed to save tenant:', error)
      toast.error(isEditMode ? 'Failed to update tenant' : 'Failed to create tenant')
    } finally {
      setSaving(false)
    }
  }, [formData, status, paymentProvider, isEditMode, tenantId, markAsSaved, broadcast, onSuccess, updateDrawer, drawerId, closeDrawer, validateForm])

  // Keyboard shortcuts
  useDrawerKeyboard(drawerId, {
    onSave: handleSave,
    onSubmit: handleSave,
  })

  // Store form state for footer access
  useEffect(() => {
    formStates.set(drawerId, {
      handleSave,
      isDirty,
      saving,
    })
    
    return () => {
      formStates.delete(drawerId)
    }
  }, [drawerId, handleSave, isDirty, saving])

  const handleInputChange = (field: keyof CreateTenantDto, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // Lender management functions
  const handleAddLender = async (lender: Lender) => {
    if (!tenantId) return

    try {
      const assigned = await lendersService.assignToTenant(tenantId, lender.id, tenantLenders.length)
      setTenantLenders(prev => [...prev, assigned])
      setShowLenderSearch(false)
      setLenderSearchQuery('')
      toast.success(`${lender.name} added`)
    } catch (error) {
      console.error('Failed to add lender:', error)
      toast.error('Failed to add lender')
    }
  }

  const handleRemoveLender = async (tenantLender: TenantLender) => {
    if (!tenantId) return

    try {
      await lendersService.removeFromTenant(tenantId, tenantLender.lender_id)
      setTenantLenders(prev => prev.filter(tl => tl.id !== tenantLender.id))
      toast.success('Lender removed')
    } catch (error) {
      console.error('Failed to remove lender:', error)
      toast.error('Failed to remove lender')
    }
  }

  const handleCreateLender = async () => {
    if (!newLenderData.name.trim() || !newLenderData.url.trim()) {
      toast.error('Name and URL are required')
      return
    }

    setSavingLender(true)
    try {
      const created = await lendersService.create(newLenderData)
      setAllLenders(prev => [...prev, created])

      // Auto-assign to tenant if in edit mode
      if (tenantId) {
        await handleAddLender(created)
      }

      setShowCreateLenderDialog(false)
      setNewLenderData({ name: '', description: '', url: '', project_type: 'commercial' })
      toast.success('Lender created successfully')
    } catch (error) {
      console.error('Failed to create lender:', error)
      toast.error('Failed to create lender')
    } finally {
      setSavingLender(false)
    }
  }

  // Filter lenders for search (exclude already assigned)
  const assignedLenderIds = tenantLenders.map(tl => tl.lender_id)
  const availableLenders = allLenders.filter(
    l => !assignedLenderIds.includes(l.id) &&
    (l.name.toLowerCase().includes(lenderSearchQuery.toLowerCase()) ||
     l.description?.toLowerCase().includes(lenderSearchQuery.toLowerCase()))
  )

  if (loading) {
    return <DrawerSkeleton sections={4} />
  }

  return (
    <div className="space-y-6">
      {/* Company Information */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Company Information</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="company_name">Company Name *</Label>
            <Input
              id="company_name"
              value={formData.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              placeholder="Enter company name"
              className="mt-1"
            />
          </div>
          
          {isEditMode && (
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="status">Status</Label>
                <Select value={status} onValueChange={(value: 'active' | 'suspended' | 'trial') => setStatus(value)}>
                  <SelectTrigger id="status" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-1">
                <Label htmlFor="plan">Subscription Plan</Label>
                <Select value={plan} onValueChange={setPlan}>
                  <SelectTrigger id="plan" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Basic">Basic</SelectItem>
                    <SelectItem value="Professional">Professional</SelectItem>
                    <SelectItem value="Enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {isEditMode && (
            <div>
              <Label htmlFor="payment-provider">Payment Processor</Label>
              <Select
                value={paymentProvider}
                onValueChange={(value: 'stripe' | 'nmi') => setPaymentProvider(value)}
                disabled={hasBlockingSubscription}
              >
                <SelectTrigger id="payment-provider" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stripe">Stripe</SelectItem>
                  <SelectItem value="nmi">NMI</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {hasBlockingSubscription
                  ? 'Locked while this tenant has an active or pending subscription — cancel it first to switch processors.'
                  : 'New subscriptions for this tenant will be billed through this processor.'}
              </p>
            </div>
          )}
        </div>
      </div>

      <Separator />

      {/* Contact Information */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <User className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Contact Information</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="contact_name">Contact Name *</Label>
            <Input
              id="contact_name"
              value={formData.contact_name}
              onChange={(e) => handleInputChange('contact_name', e.target.value)}
              placeholder="Enter contact name"
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="contact_email">Contact Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                placeholder="email@example.com"
                className="mt-1 pl-10"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="contact_phone">Contact Phone</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="contact_phone"
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                placeholder="+1 (555) 000-0000"
                className="mt-1 pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Lenders Section - Only show in edit mode */}
      {isEditMode && (
        <>
          <Separator />
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Landmark className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">Lenders</h3>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowLenderSearch(!showLenderSearch)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>

            {/* Lender Search/Add Dropdown */}
            {showLenderSearch && (
              <div className="mb-4 p-3 border rounded-lg bg-muted/30">
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search lenders..."
                    value={lenderSearchQuery}
                    onChange={(e) => setLenderSearchQuery(e.target.value)}
                    className="pl-10"
                    autoFocus
                  />
                </div>

                <div className="max-h-32 overflow-y-auto space-y-1">
                  {availableLenders.length > 0 ? (
                    availableLenders.map(lender => (
                      <button
                        key={lender.id}
                        type="button"
                        onClick={() => handleAddLender(lender)}
                        className="w-full flex items-center justify-between p-2 text-left rounded hover:bg-accent text-sm"
                      >
                        <div>
                          <span className="font-medium">{lender.name}</span>
                          <span className="ml-2 text-xs text-muted-foreground capitalize">
                            ({lender.project_type})
                          </span>
                        </div>
                        <Plus className="h-4 w-4 text-muted-foreground" />
                      </button>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      {lenderSearchQuery ? 'No matching lenders' : 'No lenders available'}
                    </p>
                  )}
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => setShowCreateLenderDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create New Lender
                </Button>
              </div>
            )}

            {/* Assigned Lenders List */}
            {tenantLenders.length > 0 ? (
              <div className="space-y-2">
                {tenantLenders.map(tl => (
                  <div
                    key={tl.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">
                          {tl.lender?.name || 'Unknown Lender'}
                        </span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary capitalize">
                          {tl.lender?.project_type || 'N/A'}
                        </span>
                      </div>
                      {tl.lender?.url && (
                        <a
                          href={tl.lender.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mt-0.5"
                        >
                          {tl.lender.url.replace(/^https?:\/\//, '').split('/')[0]}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveLender(tl)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No lenders assigned. Click "Add" to assign lenders.
              </p>
            )}
          </div>
        </>
      )}

      {/* Create Lender Dialog */}
      <Dialog open={showCreateLenderDialog} onOpenChange={setShowCreateLenderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Lender</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="lender_name">Name *</Label>
              <Input
                id="lender_name"
                value={newLenderData.name}
                onChange={(e) => setNewLenderData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Sunstone Credit"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="lender_url">URL *</Label>
              <Input
                id="lender_url"
                value={newLenderData.url}
                onChange={(e) => setNewLenderData(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="lender_description">Description</Label>
              <Textarea
                id="lender_description"
                value={newLenderData.description || ''}
                onChange={(e) => setNewLenderData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the lender..."
                className="mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="lender_project_type">Project Type *</Label>
              <Select
                value={newLenderData.project_type}
                onValueChange={(value: 'commercial' | 'residential' | 'both') =>
                  setNewLenderData(prev => ({ ...prev, project_type: value }))
                }
              >
                <SelectTrigger id="lender_project_type" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="residential">Residential</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateLenderDialog(false)}
              disabled={savingLender}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateLender} disabled={savingLender}>
              {savingLender ? 'Creating...' : 'Create Lender'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Metadata for edit mode */}
      {isEditMode && tenant && (
        <>
          <Separator />
          <div>
            <h3 className="text-lg font-semibold mb-4">Metadata</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>Created: {new Date(tenant.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>Updated: {new Date(tenant.updated_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                <span>ID: {tenant.tenant_id}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Status indicator */}
      {isDirty && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm text-yellow-800">
            You have unsaved changes. Press <kbd className="px-1 py-0.5 text-xs bg-yellow-100 rounded">{navigator.platform.toUpperCase().includes('MAC') ? 'Cmd' : 'Ctrl'}+S</kbd> to save.
          </p>
        </div>
      )}
    </div>
  )
}

// Footer component that accesses form state
export function TenantFormFooter({ isEditMode, drawerId }: { 
  isEditMode: boolean
  drawerId: string
  tenantId?: string 
}) {
  const { closeDrawer } = useDrawer()
  const [formState, setFormState] = useState(formStates.get(drawerId))
  
  useEffect(() => {
    const interval = setInterval(() => {
      setFormState(formStates.get(drawerId))
    }, 100)
    
    return () => clearInterval(interval)
  }, [drawerId])

  return (
    <DrawerFooter>
      <Button
        variant="outline"
        onClick={() => closeDrawer(drawerId)}
        disabled={formState?.saving}
      >
        Cancel
      </Button>
      <Button
        onClick={() => formState?.handleSave()}
        disabled={formState?.saving || (!isEditMode && !formState?.isDirty)}
      >
        {formState?.saving ? 'Saving...' : isEditMode ? 'Update Tenant' : 'Create Tenant'}
      </Button>
    </DrawerFooter>
  )
}